import { useMutation, useSuspenseQuery } from '@apollo/client';
import React from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Caption,
  Dialog,
  DialogContent,
  DialogTrigger,
  HStack,
  Heading,
  NewButton,
  Switch,
  Text,
  VStack,
  useToast,
} from '@eluve/components';
import {
  useIsTemplatePublisher,
  useTenantIdFromParams,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import { templateOrgsFragment } from '../operations';
import { useIsTemplateDraft } from '../useIsTemplateDraft';

import { PublishTemplateToUsers } from './PublishTemplateToUsers';
import {
  getTenantOutputTemplatesQuery,
  updateTenantTemplateAccessMutation,
} from './publishing.operations';

export interface TemplatePublishingDialog {
  templateName: string;
  templateId: string;
  isCompact?: boolean;
}

export const TemplatePublishingDialog: React.FC<TemplatePublishingDialog> = ({
  templateName,
  templateId,
  isCompact = true,
}) => {
  const toast = useToast();
  const tenantId = useTenantIdFromParams();

  useSuspenseQuery(getTenantOutputTemplatesQuery, {
    variables: {
      tenantId: tenantId!,
    },
  });

  const isUserTemplatePublisher = useIsTemplatePublisher();

  const isOrgTemplatePublisher = useIsFeatureFlagEnabled('TEMPLATE_PUBLISHING');

  const isTemplatePublisher = isOrgTemplatePublisher && isUserTemplatePublisher;

  const templateOrgData = useCompleteFragment({
    fragment: templateOrgsFragment,
    key: {
      id: templateId,
    },
  });

  const { isDraft } = useIsTemplateDraft(templateId);

  const [updateOrgShareStatus] = useMutation(
    updateTenantTemplateAccessMutation,
  );

  const orgSettings = templateOrgData?.tenant_llm_output_templates.find(
    (t) => t.tenantId === tenantId,
  );
  const isSharedWithOrg = orgSettings?.isEnabled ?? false;

  const { isDialogOpen, setIsDialogOpen } = useDialog();

  const handleShareWithOrgToggle = async (shareWithOrg: boolean) => {
    if (!isTemplatePublisher) {
      return;
    }

    await updateOrgShareStatus({
      variables: {
        tenantId: tenantId!,
        llmOutputTemplateId: templateId,
        isEnabled: shareWithOrg,
      },
      optimisticResponse: {
        insertTenantLlmOutputTemplatesOne: {
          __typename: 'TenantLlmOutputTemplates' as const,
          tenantId: tenantId!,
          llmOutputTemplateId: templateId,
          isEnabled: shareWithOrg,
          createdAt: new Date().toISOString(),
        },
      },
      onCompleted: () => {
        toast.success('Sharing updated');

        cacheUtils.updateFragment(
          {
            fragment: templateOrgsFragment,
            key: { id: templateId },
          },
          (existing) => {
            if (!existing) {
              return {
                __typename: 'LlmOutputTemplates' as const,
                id: templateId,
                tenant_llm_output_templates: [
                  {
                    __typename: 'TenantLlmOutputTemplates' as const,
                    tenantId: tenantId!,
                    llmOutputTemplateId: templateId,
                    isEnabled: shareWithOrg,
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            }

            const otherOrgs = existing.tenant_llm_output_templates.filter(
              (t) => t.tenantId !== tenantId,
            );
            return {
              __typename: 'LlmOutputTemplates' as const,
              id: templateId,
              tenant_llm_output_templates: [
                ...otherOrgs,
                {
                  __typename: 'TenantLlmOutputTemplates' as const,
                  tenantId: tenantId!,
                  llmOutputTemplateId: templateId,
                  isEnabled: shareWithOrg,
                  createdAt: new Date().toISOString(),
                },
              ],
            };
          },
        );
      },
    });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <NewButton
          type="outline"
          size={isCompact ? 's' : 'm'}
          icon={{ name: 'UserPlus' }}
          onClick={() => setIsDialogOpen(true)}
          text={isCompact ? undefined : 'Share Template'}
          disabled={isDraft}
        />
      </DialogTrigger>
      <DialogContent className="max-w-2xl pb-8">
        <VStack gap={5}>
          <Heading size="lg">Share {templateName}</Heading>
          <Text size="md" color="secondary">
            When a template is shared with users, they will be able to choose
            this template when they start a session.
          </Text>

          <PublishTemplateToUsers templateId={templateId} />

          <VStack>
            <Caption size="md" weight="semibold" color="tertiary">
              GENERAL ACCESS
            </Caption>

            {isTemplatePublisher && (
              <HStack>
                <Switch
                  checked={isSharedWithOrg}
                  isCompact
                  onCheckedChange={handleShareWithOrgToggle}
                />
                <Text size="md" color="secondary">
                  Share with all users in this organization
                </Text>
              </HStack>
            )}

            {!isTemplatePublisher && (
              <Text size="md" color="tertiary">
                This template is {isSharedWithOrg ? '' : 'not'} shared with all
                users in this organization.
              </Text>
            )}
          </VStack>
        </VStack>
      </DialogContent>
    </Dialog>
  );
};
